// withViewportWidth — utility para tornar reativo o uso de window.innerWidth.
//
// CONTEXTO: várias pages e componentes usavam `window.innerWidth > X` direto
// no render() para decidir entre layout desktop vs mobile (ex: ResponsiveTable
// vs MobileCard). React não re-renderiza no resize da janela, então depois de
// uma transição mobile -> desktop o componente ficava preso no layout antigo
// até F5. Visível em Home/Farm/Corral/Batch + Prompt/DropdownButton.
//
// USO:
//   Function component:
//     const viewportWidth = useViewportWidth()
//     if (viewportWidth > 767) ...
//
//   Class component (HOC):
//     export default withViewportWidth(MyComponent)
//     // dentro do componente: this.props.viewportWidth
//
// Implementação ouve resize com rAF (single frame coalescing) — barato.

import React from 'react'

function readWidth() {
  if (typeof window === 'undefined') return 1024
  return window.innerWidth
}

export function useViewportWidth() {
  const [width, setWidth] = React.useState(readWidth)

  React.useEffect(() => {
    let raf = null
    const handle = () => {
      if (raf !== null) return
      raf = requestAnimationFrame(() => {
        setWidth(readWidth())
        raf = null
      })
    }
    window.addEventListener('resize', handle)
    return () => {
      window.removeEventListener('resize', handle)
      if (raf !== null) cancelAnimationFrame(raf)
    }
  }, [])

  return width
}

export function withViewportWidth(Component) {
  function Wrapped(props) {
    const viewportWidth = useViewportWidth()
    return <Component {...props} viewportWidth={viewportWidth} />
  }
  Wrapped.displayName = `withViewportWidth(${Component.displayName || Component.name || 'Component'})`
  return Wrapped
}
